import React, { useEffect, useRef, useId, useContext } from 'react';
import CRMRequestContext from './CRMRequestContext';
import { scrollCRMContentIntoView } from './CRMMobile';
import { createPortal } from 'react-dom';

// Both drawers and forms share focus containment, Escape and focus restoration.
export default function CRMModal({ title, subtitle, onClose, children, wide = false, eyebrow = 'CRM WORKSPACE', className = '' }) {
    const busy = useContext(CRMRequestContext);
    const ref = useRef(null);
    const titleId = useId();
    const closeRef = useRef(onClose);
    const returnFocusRef = useRef(document.activeElement);
    useEffect(() => { closeRef.current = onClose; }, [onClose]);
    const revealInvalidField = event => {
        const field = event.target;
        let revealed = false;
        for (let parent = field.parentElement; parent && parent !== ref.current; parent = parent.parentElement) {
            if (parent.tagName === 'DETAILS' && !parent.open) { parent.open = true; revealed = true; }
        }
        if (revealed) requestAnimationFrame(() => { scrollCRMContentIntoView(field); field.focus({ preventScroll: true }); });
    };
    useEffect(() => {
        const previous = returnFocusRef.current;
        const root = ref.current;
        const shell = document.querySelector('.authenticated-shell');
        const wasInert = shell?.hasAttribute('inert');
        shell?.setAttribute('inert', '');
        const oldOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const visible = el => !el.matches(':disabled') && !el.hidden && el.getClientRects().length > 0 && !el.closest('[hidden]') && (!el.closest('details:not([open])') || el === el.closest('details:not([open])').querySelector('summary'));
        const firstField = [...root.querySelectorAll('[autofocus], input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea, select')].find(visible);
        (firstField || [...root.querySelectorAll('button')].find(visible))?.focus({ preventScroll: true });
        const keydown = event => {
            const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
            if (dialogs[dialogs.length - 1] !== root) return;
            if (root.getAttribute('aria-busy') === 'true') return;
            if (event.key === 'Escape') {
                // Native pickers handle their own Escape before the dialog closes.
                // Older browsers do not support the :open selector.
                try { if (root.querySelector('select:open')) return; } catch { /* Native picker fallback. */ }
                event.preventDefault(); closeRef.current();
            }
            if (event.key !== 'Tab') return;
            const focusable = [...root.querySelectorAll('button, a[href], input, select, textarea, summary, [tabindex="0"]')].filter(visible);
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (!root.contains(document.activeElement)) { event.preventDefault(); (event.shiftKey ? last : first)?.focus(); }
            else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        };
        document.addEventListener('keydown', keydown);
        return () => {
            document.removeEventListener('keydown', keydown);
            if (!wasInert) shell?.removeAttribute('inert');
            document.body.style.overflow = oldOverflow;
            if (previous?.isConnected) previous.focus({ preventScroll: true });
        };
    }, []);
    return createPortal(
        <div className="crm-ui crm-overlay">
            <section ref={ref} role="dialog" aria-modal="true" aria-busy={busy} aria-labelledby={titleId} onInvalidCapture={revealInvalidField} className={`crm-modal ${wide ? 'crm-modal-wide' : ''} ${className}`}>
                <header className="crm-modal-head">
                    <div><span className="crm-eyebrow">{eyebrow}</span><h2 id={titleId}>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
                    <button disabled={busy} type="button" className="crm-icon-button" onClick={onClose} aria-label="Close dialog">×</button>
                </header>
                <fieldset className="crm-request-frame" disabled={busy}>{children}</fieldset>
            </section>
        </div>, document.body
    );
}
